import Link from 'next/link'
import React, { useState } from 'react'

import { Button } from '@/components/Button/Button'
import { useStoriesUpdate } from '@/shared/api/generated/stories/stories'
import { StoryList } from '@/shared/api/models'
import { Icon } from '@/shared/components/Icon/Icon'
import { SharePopup } from '@/shared/components/SharePopup/SharePopup'
import { pxToRem } from '@/shared/styles/themes'
import { faBook } from '@fortawesome/pro-light-svg-icons/faBook'
import { faLockOpen } from '@fortawesome/pro-solid-svg-icons/faLockOpen'
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint'
import { Box, Chip, styled, Typography } from '@mui/material'

const HOVER_CLASS = 'story-card-container'



export type StoryCardProps = {
  story: StoryList
  href?: string
  onClick?: () => void
  onAddToCart?: (e: React.MouseEvent) => void
  onPreviewPrint?: (e: React.MouseEvent) => void
}

function getAspectRatio(story: StoryList): string {
  const dims = story.print_dimensions

  if (dims?.page_width_mm && dims?.page_height_mm) {
    return `${dims.page_width_mm} / ${dims.page_height_mm}`
  }

  return '1 / 1'
}


const StoryShareButton: React.FC<{ story: StoryList }> = ({ story }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const existingUrl = story.is_public && story.public_share_slug
    ? `${origin}/share/${story.public_share_slug}`
    : null

  const [shareUrl, setShareUrl] = useState<string | null>(existingUrl)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { mutate: makePublic, isPending } = useStoriesUpdate()
  
  
  return (
    <SharePopup
      url={shareUrl ?? ''}
      title={story.title || 'My Pixie World Story'}
      enabledPlatforms={{ twitter: true, facebook: true, whatsapp: true, email: true }}
      disabled={isPending}
      {...(!shareUrl && {
        onClick: (e) => {
          e.stopPropagation()
          e.preventDefault()

          const anchor = e.currentTarget

          makePublic(
            { id: story.id, data: { is_public: true } },
            {
              onSuccess: (response) => {
                const url =
              response.story?.share_url ||
              (response.story?.public_share_slug
                ? `${origin}/share/${response.story.public_share_slug}`
                : null)

                if (url) {
                  setShareUrl(url)
                  setAnchorEl(anchor)
                }
              },
              onError: () => console.error('Failed to make story public'),
            },
          )
        },
      })}
      slotProps={{
        popover: anchorEl ? {
          open: true,
          anchorEl,
          onClose: () => setAnchorEl(null),
        } : undefined,
      }}
    />
  )
}

export const StoryCard: React.FC<StoryCardProps> = ({ story, href, onClick, onAddToCart, onPreviewPrint }) => {
  const aspectRatio = getAspectRatio(story)

  const cardContent = (
    <StyledBookWrapper>
      <StyledBookCover3DContainer>
        {/* Pages are physically stacked behind the cover using translateZ */}
        <StyledPage />
        <StyledPage />
        <StyledPage />
        
        <StyledBookCover
          sx={{
            aspectRatio,
            ...(story.cover_image_url && {
              backgroundImage: `url(${story.cover_image_url})`,
            }),
          }}
        >
          <StyledSpine />
          {!story.cover_image_url && (
            <StyledPlaceholderContent>
              <Icon icon={faBook} color="disabled" sx={{ fontSize: 48, opacity: 0.3 }} />
            </StyledPlaceholderContent>
          )}
        </StyledBookCover>
      </StyledBookCover3DContainer>
      <StyledMeta>
        <StyledCardTitleShare>
          <StyledTitle variant="body1" noWrap>
            {story.title || 'Untitled Story'}
          </StyledTitle>
          <Box
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >

            {story.status === 'COMPLETE' && !story.is_preview && !story.is_locked && (
              <StoryShareButton story={story}/>
            )}

          </Box>

        </StyledCardTitleShare>
        
        {story.reader_profile_name && (
          <Typography variant="caption" color="text.secondary" noWrap>
            For: {story.reader_profile_name}
          </Typography>
        )}

        
        <StyledChipContainer>
          {story.interested_in_book && (
            <Chip label="Print Requested" size="small" color="primary" variant="outlined" />
          )}
          {story.is_favorite && (
            <Chip label="Favorite" size="small" color="secondary" variant="outlined" />
          )}
          {story.is_preview && (
            <Chip label="Preview" size="small" color="info" variant="outlined" />
          )}
          {story.is_locked && (
            <Chip label="Locked" size="small" color="warning" variant="outlined" />
          )}

        </StyledChipContainer>

        {onAddToCart && (
          <Box sx={{ mt: 1 }}>
            <Button
              primary
              startIcon={<Icon icon={faLockOpen} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                e.preventDefault()
                onAddToCart(e)
              }}
              sx={{ width: '100%' }}
            >
              Unlock Story
            </Button>
          </Box>
        )}

        {onPreviewPrint && (
          <Box sx={{ mt: 1 }}>
            <Button
              primary
              startIcon={<Icon icon={faPrint} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                e.preventDefault()
                onPreviewPrint(e)
              }}
              sx={{ width: '100%' }}
            >
              Preview & Print
            </Button>
          </Box>
        )}
      </StyledMeta>
    </StyledBookWrapper>
  )

  if (href) {
    return (
      <StyledCardLink href={href} className={HOVER_CLASS}>
        {cardContent}
      </StyledCardLink>
    )
  }

  return (
    <StyledCardWrapper onClick={onClick} className={HOVER_CLASS}>
      {cardContent}
    </StyledCardWrapper>
  )
}

// --- Styled Components ---

const StyledCardTitleShare = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${pxToRem(3)} 
` 
  

const StyledCardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`

const StyledCardWrapper = styled('div')`
  cursor: pointer;
`

const StyledBookWrapper = styled('div')``

const StyledBookCover3DContainer = styled('div')`
  perspective: 1500px;
  position: relative;
  transform-style: preserve-3d;
  width: 100%;
`

/* Heavy paper physics: Fast start, slow settle */
const bookPhysics = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

const StyledPage = styled('div')`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  width: 98%;
  background-color: #fff;
  border: 1px solid rgba(0,0,0,0.05); /* Subtle border to define edges */
  border-left: none;
  border-radius: 0 ${pxToRem(3)} ${pxToRem(3)} 0;
  transform-origin: left center;
  
  /* 
     Fix for "fading in": 
     We give pages a resting TranslateZ. This puts them physically behind the cover
     in 3D space, preventing z-fighting (flickering) when the cover is closed.
  */
  transition: transform 0.6s ${bookPhysics};

  /* Page 1 (Topmost under cover) */
  &:nth-of-type(3) {
    z-index: 3;
    background: #fdfbf7;
    transform: translateZ(-2px); /* Sit 2px behind cover */
    
    .${HOVER_CLASS}:hover & {
      /* Very subtle lift */
      transform: rotateY(-6deg) translateZ(-2px);
    }
  }

  /* Page 2 */
  &:nth-of-type(2) {
    z-index: 2;
    background: #f8f6f1;
    transform: translateZ(-4px); /* Sit 4px behind cover */
    
    .${HOVER_CLASS}:hover & {
      transform: rotateY(-4deg) translateZ(-4px);
    }
  }

  /* Page 3 (Deepest) */
  &:nth-of-type(1) {
    z-index: 1;
    background: #eff0eb;
    transform: translateZ(-6px); /* Sit 6px behind cover */
    
    .${HOVER_CLASS}:hover & {
      transform: rotateY(-2deg) translateZ(-6px);
    }
  }
`

const StyledBookCover = styled('div')`
  position: relative;
  width: 100%;
  background-color: #fff;
  background-size: cover;
  background-position: center;
  border-radius: 0 ${pxToRem(4)} ${pxToRem(4)} 0;
  overflow: hidden;
  
  /* Initial State */
  transform-origin: left center;
  transform-style: preserve-3d;
  transform: rotateY(0deg) translateZ(0);
  z-index: 10;
  
  /* Initial flat shadow */
  box-shadow: 
    0 1px 3px rgba(0,0,0,0.1),
    0 4px 8px rgba(0,0,0,0.05);

  transition: 
    transform 0.6s ${bookPhysics}, 
    box-shadow 0.6s ease;

  /* Glossy spine overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${pxToRem(12)};
    background: linear-gradient(
      to right, 
      rgba(255,255,255,0.25) 0%, 
      rgba(255,255,255,0) 40%, 
      rgba(0,0,0,0.05) 100%
    );
    pointer-events: none;
    z-index: 20;
  }

  /* HOVER ACTION */
  .${HOVER_CLASS}:hover & {
    /* 
       Reduced angle to -20deg for a "peek" effect.
       translateZ(2px) lifts it slightly to clear the pages visually.
    */
    transform: rotateY(-20deg) translateZ(2px);
    
    /* Shadow softens and moves right */
    box-shadow: 
      8px 5px 16px rgba(0, 0, 0, 0.15),
      2px 2px 4px rgba(0,0,0,0.05);
  }
`

const StyledSpine = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: ${pxToRem(6)};
  height: 100%;
  background: linear-gradient(to right, #7a664e 0%, #8B7355 50%, #7a664e 100%);
  z-index: 5;
  box-shadow: inset 1px 0 2px rgba(255,255,255,0.2);
`

const StyledPlaceholderContent = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e6e6e6;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: inherit;
`

const StyledMeta = styled('div')`
  padding: ${pxToRem(12)} ${pxToRem(2)} 0;
`

const StyledTitle = styled(Typography)`
  font-weight: 600;
  line-height: 1.3;
`

const StyledChipContainer = styled(Box)`
  display: flex;
  gap: ${pxToRem(4)};
  margin-top: ${pxToRem(6)};
  flex-wrap: wrap;
  align-items: center;  
`